import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, PaperPlaneTilt, Plus, Receipt, X } from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function InvoicingSettings() {
  const nav = useNavigate();
  const [conf, setConf] = useState(null);
  const [busy, setBusy] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestResult, setDigestResult] = useState("");
  const latest = useRef(null);

  const sendDigestNow = async (weekly) => {
    setDigestBusy(true);
    setDigestResult("");
    try {
      const { data } = await api.post(
        `/settings/invoicing/digest/send-now${weekly ? "?weekly=1" : ""}`,
      );
      if (data.skipped) {
        setDigestResult(`Skipped — ${data.skipped}.`);
      } else {
        setDigestResult(
          `${data.weekly ? "Monday edition sent" : "Sent"} to ${data.sent}/${data.recipients.length} inbox(es): ${data.received} payment(s) yesterday, ${data.outstanding} open invoice(s), ${data.overdue} overdue${
            data.weekly ? `, ${data.over30} customer(s) 30+ days` : ""
          }.`,
        );
        toast.success("Digest sent");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send the digest");
    }
    setDigestBusy(false);
  };

  const load = useCallback(async () => {
    const { data } = await api.get("/settings/invoicing");
    latest.current = data;
    setConf(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key, value) =>
    setConf((prev) => {
      const next = { ...prev, [key]: value };
      latest.current = next;
      return next;
    });

  const save = async () => {
    setBusy(true);
    try {
      const body = latest.current;
      const { data } = await api.put("/settings/invoicing", {
        ...body,
        deposit_pct: Number(body.deposit_pct),
        deposit_due_days: Number(body.deposit_due_days),
        balance_due_days: Number(body.balance_due_days),
        deposit_reminder_days: Number(body.deposit_reminder_days),
        payment_methods: (body.payment_methods || []).map((m) => m.trim()).filter(Boolean),
        payment_notify_emails: (Array.isArray(body.payment_notify_emails)
          ? body.payment_notify_emails
          : String(body.payment_notify_emails || "").split(/[,\s;]+/)
        )
          .map((e) => e.trim())
          .filter(Boolean),
        digest_emails: (Array.isArray(body.digest_emails)
          ? body.digest_emails
          : String(body.digest_emails || "").split(/[,\s;]+/)
        )
          .map((e) => e.trim())
          .filter(Boolean),
      });
      latest.current = data;
      setConf(data);
      toast.success("Invoicing settings saved");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save those settings",
      );
    }
    setBusy(false);
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Invoicing" />
      <main className="relative z-10 pb-36 pt-6">
        <button
          type="button"
          data-testid="invoicing-settings-back"
          onClick={() => nav("/settings")}
          className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-200"
        >
          <ArrowLeft size={13} weight="bold" /> Settings
        </button>
        <div className="mt-3 flex items-center gap-3">
          <Receipt size={22} weight="bold" className="text-indigo-300" />
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Invoicing
          </h1>
        </div>
        <p className="mt-1 max-w-2xl text-sm text-zinc-500">
          When a quote is accepted — in the office or by the customer in their
          portal — these settings decide the deposit and balance invoices that
          get raised.
        </p>

        {conf && (
          <div className="mt-6 space-y-4" data-testid="invoicing-settings">
            <section className="rounded-lg border border-zinc-800 bg-zinc-900 p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-zinc-100">
                    Raise invoices automatically
                  </div>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    A deposit invoice and a balance invoice the moment a quote
                    is accepted
                  </p>
                </div>
                <Switch
                  data-testid="invoicing-auto-create"
                  checked={!!conf.auto_create}
                  onCheckedChange={(v) => set("auto_create", v)}
                />
              </div>

              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Deposit %
                  </span>
                  <Input
                    data-testid="invoicing-deposit-pct"
                    type="number"
                    step="1"
                    value={conf.deposit_pct}
                    onChange={(e) => set("deposit_pct", e.target.value)}
                    className="font-mono-data mt-1 h-11 border-zinc-800 bg-zinc-950"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Deposit due in (days)
                  </span>
                  <Input
                    data-testid="invoicing-deposit-days"
                    type="number"
                    step="1"
                    value={conf.deposit_due_days}
                    onChange={(e) => set("deposit_due_days", e.target.value)}
                    className="font-mono-data mt-1 h-11 border-zinc-800 bg-zinc-950"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Balance due in (days)
                  </span>
                  <Input
                    data-testid="invoicing-balance-days"
                    type="number"
                    step="1"
                    value={conf.balance_due_days}
                    onChange={(e) => set("balance_due_days", e.target.value)}
                    className="font-mono-data mt-1 h-11 border-zinc-800 bg-zinc-950"
                  />
                </label>
              </div>
              <p className="mt-2 text-xs text-zinc-600">
                Leave balance days at 0 and the balance invoice is dated to the
                booked install day.
              </p>

              <div className="mt-5 flex items-start justify-between gap-4 border-t border-zinc-800 pt-5">
                <div>
                  <div className="text-sm font-semibold text-zinc-100">
                    Chase unpaid deposits
                  </div>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    Email the customer once if their deposit is still unpaid a
                    few days after they accept the quote
                  </p>
                </div>
                <Switch
                  data-testid="invoicing-deposit-reminder-on"
                  checked={!!conf.deposit_reminder_on}
                  onCheckedChange={(v) => set("deposit_reminder_on", v)}
                />
              </div>
              <label className="mt-4 block max-w-[16rem]">
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Remind after (days)
                </span>
                <Input
                  data-testid="invoicing-deposit-reminder-days"
                  type="number"
                  step="1"
                  value={conf.deposit_reminder_days ?? 3}
                  onChange={(e) => set("deposit_reminder_days", e.target.value)}
                  className="font-mono-data mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </label>

              <div className="mt-5 flex items-start justify-between gap-4 border-t border-zinc-800 pt-4">
                <div>
                  <div className="text-sm font-semibold text-zinc-100">
                    Itemise the balance invoice
                  </div>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    Break the balance into material, fabrication and install lines (less the
                    deposit already invoiced) so each posts to its own QuickBooks account.
                    Off = one lump balance line.
                  </p>
                </div>
                <Switch
                  data-testid="invoicing-itemize-balance"
                  checked={conf.itemize_balance !== false}
                  onCheckedChange={(v) => set("itemize_balance", v)}
                />
              </div>
            </section>

            <section className="rounded-lg border border-zinc-800 bg-zinc-900 p-5">
              <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                Wording on the invoice
              </h2>
              <label className="mt-4 block">
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Payment terms
                </span>
                <Input
                  data-testid="invoicing-terms"
                  value={conf.terms || ""}
                  onChange={(e) => set("terms", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </label>
              <label className="mt-3 block">
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Footer note
                </span>
                <Input
                  data-testid="invoicing-footer"
                  value={conf.footer_note || ""}
                  onChange={(e) => set("footer_note", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </label>
            </section>

            <section
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
              data-testid="invoicing-payment-methods"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                    Payment methods
                  </h2>
                  <p className="mt-1 text-xs text-zinc-500">
                    The "Paid by" choices when staff record a payment on an invoice — check,
                    cash, ACH and anything else you accept. Drag-free: the first one is the
                    default.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="invoicing-add-method"
                  onClick={() => set("payment_methods", [...(conf.payment_methods || []), ""])}
                  className="h-9 border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800"
                >
                  <Plus size={13} weight="bold" className="mr-1" /> Add method
                </Button>
              </div>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {(conf.payment_methods || []).map((m, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      data-testid={`invoicing-method-${i}`}
                      value={m}
                      placeholder="e.g. Zelle"
                      onChange={(e) => {
                        const next = [...conf.payment_methods];
                        next[i] = e.target.value;
                        set("payment_methods", next);
                      }}
                      className="h-11 border-zinc-800 bg-zinc-950"
                    />
                    <button
                      type="button"
                      data-testid={`invoicing-remove-method-${i}`}
                      aria-label="Remove method"
                      onClick={() =>
                        set("payment_methods", conf.payment_methods.filter((_, j) => j !== i))
                      }
                      className="flex h-11 w-10 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:border-red-500/40 hover:text-red-400"
                    >
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
              {!(conf.payment_methods || []).some((m) => m.trim()) && (
                <p className="mt-2 text-xs text-amber-300" data-testid="invoicing-methods-warning">
                  Keep at least one payment method.
                </p>
              )}
            </section>

            <section
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
              data-testid="invoicing-receipts"
            >
              <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                Payment receipts
              </h2>
              <div className="mt-4 flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-zinc-100">
                    Email the customer a receipt for every online payment
                  </div>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    The moment a portal card, Paya or QuickBooks payment lands on an invoice —
                    amount, method, remaining balance, PDF receipt attached.
                  </p>
                </div>
                <Switch
                  data-testid="invoicing-payment-receipt-on"
                  checked={conf.payment_receipt_on !== false}
                  onCheckedChange={(v) => set("payment_receipt_on", v)}
                />
              </div>
              <div className="mt-5 flex items-start justify-between gap-4 border-t border-zinc-800 pt-5">
                <div>
                  <div className="text-sm font-semibold text-zinc-100">
                    Also when staff record a payment by hand
                  </div>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    Check, cash, ACH… recorded on the invoice page. Off = the customer only hears
                    from us once the invoice is paid in full.
                  </p>
                </div>
                <Switch
                  data-testid="invoicing-receipt-on-manual"
                  checked={!!conf.receipt_on_manual}
                  onCheckedChange={(v) => set("receipt_on_manual", v)}
                />
              </div>
              <div className="mt-5 flex items-start justify-between gap-4 border-t border-zinc-800 pt-5">
                <div>
                  <div className="text-sm font-semibold text-zinc-100">
                    Send the office a copy
                  </div>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    A "Payment landed" email with customer, invoice, amount, method and what's
                    still owed — plus anyone subscribed to the Payment received alert.
                  </p>
                </div>
                <Switch
                  data-testid="invoicing-payment-notify-on"
                  checked={conf.payment_notify_on !== false}
                  onCheckedChange={(v) => set("payment_notify_on", v)}
                />
              </div>
              <label className="mt-4 block">
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Office copy to (comma-separated)
                </span>
                <Input
                  data-testid="invoicing-payment-notify-emails"
                  value={
                    Array.isArray(conf.payment_notify_emails)
                      ? conf.payment_notify_emails.join(", ")
                      : conf.payment_notify_emails || ""
                  }
                  placeholder="Leave blank to use the company email"
                  onChange={(e) => set("payment_notify_emails", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </label>
            </section>

            <section
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
              data-testid="invoicing-digest"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                    Morning payments digest
                  </h2>
                  <p className="mt-1 text-xs text-zinc-500">
                    One short email every morning at 7am (shop time): yesterday's payments with
                    method and source, then every invoice still carrying a balance — oldest due
                    first, overdue flagged. Skipped when there is nothing to report.
                  </p>
                </div>
                <Switch
                  data-testid="invoicing-digest-on"
                  checked={conf.payment_digest_on !== false}
                  onCheckedChange={(v) => set("payment_digest_on", v)}
                />
              </div>
              <div className="mt-4 flex items-start justify-between gap-4 border-t border-zinc-800 pt-4">
                <div>
                  <div className="text-sm font-semibold text-zinc-100">Monday edition</div>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    Mondays also total last week's income (with the trend vs the week before and a
                    by-method split) and list every customer 30+ days overdue, with one-click
                    "Send reminder" links next to each overdue invoice.
                  </p>
                </div>
                <Switch
                  data-testid="invoicing-digest-weekly-on"
                  checked={conf.digest_weekly_on !== false}
                  onCheckedChange={(v) => set("digest_weekly_on", v)}
                />
              </div>
              <label className="mt-4 block">
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Send to (comma-separated)
                </span>
                <Input
                  data-testid="invoicing-digest-emails"
                  value={
                    Array.isArray(conf.digest_emails)
                      ? conf.digest_emails.join(", ")
                      : conf.digest_emails || ""
                  }
                  placeholder="Leave blank to use the office copy list / company email"
                  onChange={(e) => set("digest_emails", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </label>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="invoicing-digest-send-now"
                  disabled={digestBusy}
                  onClick={() => sendDigestNow(false)}
                  className="h-9 border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800"
                >
                  <PaperPlaneTilt size={13} weight="bold" className="mr-1" />
                  {digestBusy ? "Sending…" : "Send it now"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="invoicing-digest-send-weekly"
                  disabled={digestBusy}
                  onClick={() => sendDigestNow(true)}
                  className="h-9 border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800"
                >
                  Send Monday edition
                </Button>
                {digestResult && (
                  <span className="text-xs text-zinc-400" data-testid="invoicing-digest-result">
                    {digestResult}
                  </span>
                )}
              </div>
              <p className="mt-2 text-xs text-zinc-600">
                "Send it now" uses the saved recipients — save first if you just changed them.
              </p>
            </section>

            <div className="flex justify-end">
              <Button
                data-testid="invoicing-settings-save"
                disabled={busy}
                onClick={save}
                className="h-11 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                {busy ? "Saving…" : "Save settings"}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
